import { ActionTree } from 'vuex';
import RootState from '@/store/root_state';
import { PaymentState, CreditCard } from './state';
import { setEnrollmentData } from '../enrollment/actions';
import { normalizeEnrollment } from '@/store/graphql/normalizer';
import { mutation, query, MicroserviceEnum } from '@/helpers/westeros';
import {
  addCreditCardPayment,
  AddCreditCardPaymentMutationInput,
  AddCreditCardPaymentMutationResult,
  generateBankSlip,
  GenerateBankSlipMutationResult,
  generatePixData,
  GeneratePixDataMutationResult,
  removeRecurrentCreditCard,
  RemoveRecurrentCreditCardResult,
} from '@/store/graphql/mutations/westerosArcaneumMutations';
import {
  OptionsQueryResult,
  getCreditCardYears,
  getCreditCardFlags,
} from '@/store/graphql/queries/westeros-queries-options';
import {
  getPaymentData,
  GetPaymentDataQueryResult,
  getPixPaymentStatus,
  GetPixPaymentStatusResult,
} from '@/store/graphql/queries/westerosArcaneumQueries';

const actions: ActionTree<PaymentState, RootState> = {
  /* UPDATE Actions */

  async UPDATE_CREDIT_CARD(
    { commit },
    payload: { creditCard: AddCreditCardPaymentMutationInput },
  ): Promise<void> {
    const { creditCard } = payload;

    const { data } = await mutation<AddCreditCardPaymentMutationResult>(
      MicroserviceEnum.ARCANEUM,
      addCreditCardPayment,
      { data: creditCard },
    );

    const [firstPayment] = data?.addCreditCardPayment.payment || [];

    commit('SET_CREDIT_CARD', {
      ...creditCard,
      recurrentCreditCardId: firstPayment?.backofficeRecurrentCardId,
      isRecurrentCreditCard: firstPayment?.isRecurrentCreditCard,
    });

    const enrollment = normalizeEnrollment(data?.addCreditCardPayment);
    setEnrollmentData(commit, enrollment);
  },

  /* PULL Actions */

  async PULL_FLAG_OPTIONS({ commit }) {
    const { data } = await query<OptionsQueryResult>(
      MicroserviceEnum.ARCANEUM,
      getCreditCardFlags,
      undefined,
      true,
    );

    commit('SET_FLAG_OPTIONS', data.getCreditCardFlags.options);
  },

  async PULL_YEAR_OPTIONS({ commit }) {
    const { data } = await query<OptionsQueryResult>(
      MicroserviceEnum.ARCANEUM,
      getCreditCardYears,
      undefined,
      true,
    );

    commit('SET_YEAR_OPTIONS', data.getCreditCardYears.options);
  },

  async PULL_MONTH_OPTIONS({ commit }) {
    const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
    commit('SET_MONTH_OPTIONS', months);
  },

  async PULL_PAYMENT_VALUE({ commit }) {
    const { data } = await query<GetPaymentDataQueryResult>(
      MicroserviceEnum.ARCANEUM,
      getPaymentData,
    );

    commit('SET_PAYMENT_VALUE', data.getPaymentData.paymentValue);
  },

  async GENERATE_BANK_SLIP({ commit }) {
    const { data } = await mutation<GenerateBankSlipMutationResult>(
      MicroserviceEnum.ARCANEUM,
      generateBankSlip,
    );

    commit('SET_BANK_SLIP_FILE', data?.generateBankSlip);
  },

  async GENERATE_PIX_DATA({ commit }) {
    const { data } = await mutation<GeneratePixDataMutationResult>(
      MicroserviceEnum.ARCANEUM,
      generatePixData,
    );

    commit('SET_PIX_DATA', data?.generatePixData);
  },
  async GET_PIX_STATUS({ commit }) {
    const { data } = await query<GetPixPaymentStatusResult>(
      MicroserviceEnum.ARCANEUM,
      getPixPaymentStatus,
    );

    if (data && data.getPixPaymentStatus.paymentStatus === 'paid') {
      commit('SET_ENROLLMENT_STATUS', 'success');
    }

    commit('SET_PIX_STATUS', data?.getPixPaymentStatus);
  },
  REMOVE_RECURRENT_CREDIT_CARD() {
    mutation<RemoveRecurrentCreditCardResult>(MicroserviceEnum.ARCANEUM, removeRecurrentCreditCard);
  },
};

export default actions;
